import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { BrowserRouter, HashRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { NewVersionBanner, ToastProvider } from '@dataroom/shared';
import { Layout } from '@/components/Layout';
import { AffiliatePage } from '@/pages/AffiliatePage';
import { BillingPage } from '@/pages/BillingPage';
import { ChecklistsPage } from '@/pages/ChecklistsPage';
import { HelpPage } from '@/pages/HelpPage';
import { LoginPage } from '@/pages/LoginPage';
import { PlatformAffiliatesPage } from '@/pages/PlatformAffiliatesPage';
import { PlatformKeysPage } from '@/pages/PlatformKeysPage';
import { PlatformRecoveryPage } from '@/pages/PlatformRecoveryPage';
import { RoomsPage } from '@/pages/RoomsPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { SignupPage } from '@/pages/SignupPage';
import { TeamPage } from '@/pages/TeamPage';
import { UnlockPage } from '@/pages/UnlockPage';
import { RoomAccessPage } from '@/pages/room/RoomAccessPage';
import { RoomActivityPage } from '@/pages/room/RoomActivityPage';
import { RoomAgreementsPage } from '@/pages/room/RoomAgreementsPage';
import { RoomBuyersPage } from '@/pages/room/RoomBuyersPage';
import { RoomChecklistPage } from '@/pages/room/RoomChecklistPage';
import { needsFullReloadFor } from '@/lib/analytics';
import { contractsEnabled } from '@/lib/features';
import { RoomContractsPage } from '@/pages/room/RoomContractsPage';
import { RoomDataPage } from '@/pages/room/RoomDataPage';
import { RoomIndexRedirect, RoomLayout } from '@/pages/room/RoomLayout';
import { RoomQaPage } from '@/pages/room/RoomQaPage';
import { RoomReviewPage } from '@/pages/room/RoomReviewPage';
import { RoomProcessingPage } from '@/pages/room/RoomProcessingPage';
import { RoomRedactionReviewPage } from '@/pages/room/RoomRedactionReviewPage';
import { RoomRisksPage } from '@/pages/room/RoomRisksPage';
import { RoomSettingsPage } from '@/pages/room/RoomSettingsPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, staleTime: 30_000 },
  },
});

// The hosted mock demo is served as static files (no server-side SPA
// fallback), so it uses hash routing to survive deep-link refreshes. The real
// app uses history-based routing behind the Pages/API origin.
const Router = import.meta.env.VITE_USE_MOCK === '1' ? HashRouter : BrowserRouter;

/** Offers a reload once a newer build is deployed (web/shared/src/ui/new-version-banner.tsx).
 * Off in dev and in the mock demo build, where there is no deploy to compare. */
function NewVersionNotice() {
  const { pathname } = useLocation();
  return (
    <NewVersionBanner
      currentVersion={import.meta.env.VITE_APP_VERSION}
      enabled={import.meta.env.PROD && import.meta.env.VITE_USE_MOCK !== '1'}
      routeKey={pathname}
    />
  );
}

/**
 * ADR-0069 W12: a no-analytics page (the keys console, the unlock page) entered
 * by a client-side route change while the analytics tag is loaded renders
 * nothing and reloads as a full document, so the tag is gone and the page's
 * strict CSP applies before any kit or code can be shown or typed.
 */
export function AnalyticsRouteGuard({
  children,
  reloadPage = () => window.location.reload(),
}: {
  children: ReactNode;
  /** Test seam: what a full reload does. */
  reloadPage?: () => void;
}) {
  const { pathname } = useLocation();
  const reload = needsFullReloadFor(pathname);
  useEffect(() => {
    if (reload) reloadPage();
  }, [reload, pathname, reloadPage]);
  return reload ? null : <>{children}</>;
}

/**
 * The admin route table. A room is a shell (/rooms/:roomId) with nested
 * subpages; its index is the Data room itself, so /rooms/:roomId deep links keep
 * landing on the room's contents. Exported separately from {@link App} so tests
 * can mount it under a MemoryRouter.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      {/* The affiliate portal: its own principal and chrome, outside the tenant Layout. */}
      <Route path="/affiliate" element={<AffiliatePage />} />
      {/* Unlock (ADR-0069): no sign-in and no app chrome — the service is locked. */}
      <Route path="/unlock" element={<UnlockPage />} />
      <Route element={<Layout />}>
        <Route path="/" element={<RoomsPage />} />
        <Route path="/rooms/:roomId" element={<RoomLayout />}>
          <Route index element={<RoomDataPage />} />
          <Route path="review" element={<RoomReviewPage />} />
          <Route path="processing" element={<RoomProcessingPage />} />
          <Route path="redaction" element={<RoomRedactionReviewPage />} />
          <Route path="checklist" element={<RoomChecklistPage />} />
          <Route path="buyers" element={<RoomBuyersPage />} />
          <Route path="qa" element={<RoomQaPage />} />
          <Route path="risks" element={<RoomRisksPage />} />
          {/* Off on PROD (VITE_FEATURE_CONTRACTS=0): the room's catch-all below lands a deep link on the room home. */}
          {contractsEnabled() ? <Route path="contracts" element={<RoomContractsPage />} /> : null}
          <Route path="agreements" element={<RoomAgreementsPage />} />
          <Route path="access" element={<RoomAccessPage />} />
          <Route path="activity" element={<RoomActivityPage />} />
          <Route path="settings" element={<RoomSettingsPage />} />
          <Route path="*" element={<RoomIndexRedirect />} />
        </Route>
        <Route path="/help/*" element={<HelpPage />} />
        <Route path="/checklists" element={<ChecklistsPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/platform/affiliates" element={<PlatformAffiliatesPage />} />
        <Route path="/platform/ownership-recovery" element={<PlatformRecoveryPage />} />
        <Route path="/platform/keys" element={<PlatformKeysPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <Router>
          <AnalyticsRouteGuard>
            <AppRoutes />
          </AnalyticsRouteGuard>
          <NewVersionNotice />
        </Router>
      </ToastProvider>
    </QueryClientProvider>
  );
}
